import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ContactRepository } from '../../states/contact.repository';
import { AnimalContactRelationRepository } from '../../states/animal-contact-relation.repository';
import {
  AnimalContactRelationAnimal,
  AnimalContactRelationModel,
} from '../../models/animal-contact-relation.model';
import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalRepository } from 'src/app/modules/animals/states/animal.repository';
import { forkJoin, of, switchMap, take, takeWhile, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { Location } from '@angular/common';
import { AnimalContactTypeEnum } from 'src/app/modules/animals/models/contact-type.enum';
import { AnimalListItemContext } from 'src/app/shared/components/animal-list-item/animal-list-item.component';

/**
 * Gruppiert wird nach der Rolle des Kontakts, nicht nach der Event-Kategorie:
 * der Event-Typ steht ohnehin je Zeile in der Spalte Herkunft/Verbleib, und die
 * Kategorie des Backends (AnimalContactCategory) kennt Rollen wie "Besitzer" oder
 * "Pflegefamilie auf Probe" gar nicht.
 *
 * Diese Liste bestimmt die Reihenfolge der Akkordeons. Angezeigt werden nur
 * Rollen, die Tiere enthalten. Unbekannte Rollen aus dem Backend werden hinten
 * angehaengt.
 */
const ROLE_ORDER: AnimalContactTypeEnum[] = [
  AnimalContactTypeEnum.OWNER,
  AnimalContactTypeEnum.FINDER,
  AnimalContactTypeEnum.GIVER,
  AnimalContactTypeEnum.PERSON_TAKING_OVER,
  AnimalContactTypeEnum.FOSTER_FAMILY,
  AnimalContactTypeEnum.TRIAL_FAMILY,
  AnimalContactTypeEnum.AUTHORIZED_PICKUP,
  AnimalContactTypeEnum.SUPERVISOR,
  AnimalContactTypeEnum.PARTNER,
  AnimalContactTypeEnum.PARENT,
  AnimalContactTypeEnum.SON_DAUGHTER,
  AnimalContactTypeEnum.BROTHER_SISTER,
  AnimalContactTypeEnum.NEIGHBOR,
  AnimalContactTypeEnum.ADDITIONAL_PARTY,
];

/** Sammelgruppe fuer Relationen ohne gesetzten contactType. */
const UNKNOWN_ROLE = 'unknown';

interface ContactAnimalRelationGroup {
  key: string;
  label: string;
  animals: AnimalModel[];
  context: Map<number, AnimalListItemContext>;
}

@Component({
  selector: 'app-contact-person-details',
  templateUrl: './contact-person-details.component.html',
  standalone: false,
})
export class ContactPersonDetailsComponent implements OnInit, OnDestroy {
  private alive = true;
  contact: any;
  editingContact: any;

  relations: AnimalContactRelationModel[] = [];
  animalsMap: Map<number, AnimalModel> = new Map();

  relationGroups: ContactAnimalRelationGroup[] = [];
  accordionValue: string[] = [];

  openEdit = false;
  validationStatusChanged = false;
  existingContactUpdated = false;

  loadingRelations = true;
  selectedTabIndex: number = 0;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly _contactRepository: ContactRepository,
    private readonly _animalContactRelationRepository: AnimalContactRelationRepository,
    private readonly _animalRepository: AnimalRepository,
    private readonly _transloco: TranslocoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _location: Location,
  ) { }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;

    this.loadingRelations = true;

    this._contactRepository
      .getById(id)
      .pipe(
        takeWhile(() => this.alive),
        tap(contact => (this.contact = contact)),
        switchMap(contact => this._animalContactRelationRepository.getByContactId(contact.id)),
        switchMap(relations => this.resolveAnimals(relations).pipe(
          map(animalsMap => ({ relations, animalsMap }))
        )),
        tap(({ relations, animalsMap }) => {
          this.relations = relations;
          this.animalsMap = animalsMap;

          this.buildRelationGroups();

          this.loadingRelations = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe();

    this.route.queryParamMap
      .pipe(takeWhile(() => this.alive))
      .subscribe(qp => {
        this.selectedTabIndex = Number(qp.get('tabNumber')) || 0;
      });
  }

  /**
   * Nutzt das eingebettete `animal` der Relation. Nur fuer Relationen ohne
   * eingebettetes Tier wird auf /animals/{id} zurueckgefallen.
   */
  private resolveAnimals(relations: AnimalContactRelationModel[]) {
    const animalsMap = new Map<number, AnimalModel>();
    const missingIds = new Set<number>();

    for (const relation of relations) {
      const animalId = this.getAnimalId(relation);
      if (!animalId || animalsMap.has(animalId)) continue;

      if (relation.animal) {
        animalsMap.set(animalId, this.toAnimalModel(relation.animal));
      } else {
        missingIds.add(animalId);
      }
    }

    if (missingIds.size === 0) return of(animalsMap);

    return forkJoin(
      Array.from(missingIds).map(animalId =>
        this._animalRepository.getById(animalId, true).pipe(
          take(1),
          map(animal => ({ animalId, animal }))
        )
      )
    ).pipe(
      map(results => {
        results.forEach(({ animalId, animal }) => {
          if (animal) animalsMap.set(animalId, animal);
        });
        return animalsMap;
      })
    );
  }

  private toAnimalModel(animal: AnimalContactRelationAnimal): AnimalModel {
    return animal as unknown as AnimalModel;
  }

  /** Das Backend liefert die Tier-ID in `animal.id`, nicht als flaches Feld. */
  private getAnimalId(relation: AnimalContactRelationModel): number | undefined {
    return relation?.animal?.id ?? relation?.animalId;
  }

  private buildRelationGroups(): void {
    const byRole = new Map<string, AnimalContactRelationModel[]>();

    for (const relation of this.relations) {
      const role = relation?.contactType ?? UNKNOWN_ROLE;
      const bucket = byRole.get(role);
      if (bucket) bucket.push(relation);
      else byRole.set(role, [relation]);
    }

    const roles: string[] = [...ROLE_ORDER];

    for (const role of byRole.keys()) {
      if (!roles.includes(role)) roles.push(role);
    }

    this.relationGroups = roles
      .map(role => this.buildGroup(role, byRole.get(role) ?? []))
      .filter(group => group.animals.length > 0);

    this.accordionValue = this.relationGroups.map((_, idx) => idx.toString());
  }

  private buildGroup(role: string, relations: AnimalContactRelationModel[]): ContactAnimalRelationGroup {
    const animals: AnimalModel[] = [];
    const context = new Map<number, AnimalListItemContext>();
    const seen = new Set<number>();

    for (const relation of relations) {
      const animalId = this.getAnimalId(relation);
      if (!animalId || seen.has(animalId)) continue;

      const animal = this.animalsMap.get(animalId);
      if (!animal) continue;

      seen.add(animalId);
      animals.push(animal);
      context.set(animalId, this.toListItemContext(relation));
    }

    return {
      key: role,
      label: this.getRoleLabel(role),
      animals,
      context,
    };
  }

  /** Macht aus der Relation die Herkunft-/Verbleib-Spalte der Tierliste. */
  private toListItemContext(relation: AnimalContactRelationModel): AnimalListItemContext {
    if (relation.relationType === 'origin') {
      return {
        origin: {
          type: relation.eventType,
          location: relation.location ?? undefined,
          dateOfReceipt: relation.eventDate,
        },
      };
    }

    return {
      whereabout: {
        type: relation.eventType,
        location: relation.location ?? undefined,
        date: relation.eventDate,
        contact: { id: relation.contactId },
      },
    };
  }

  /** Rollen-Label aus `animalContact.types.*`, sonst der rohe Key. */
  private getRoleLabel(role: string): string {
    if (role === UNKNOWN_ROLE) {
      return this._transloco.translate('contactDetails.relationRole.unknown');
    }

    const key = `animalContact.types.${role}`;
    const translated = this._transloco.translate(key);
    return translated && translated !== key ? translated : role;
  }

  onTabOpenChange(event: number | string | undefined) {
    const tabNumber = typeof event === 'number' ? event : parseInt(event as string, 10);
    this.selectedTabIndex = tabNumber;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tabNumber },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  openEditDialog(): void {
    this.openEdit = true;
    this.editingContact = JSON.parse(JSON.stringify(this.contact));
  }

  onValidationStatusChanged($event: boolean) {
    this.validationStatusChanged = $event;
  }

  onExistingContactUpdated($event: boolean) {
    this.existingContactUpdated = $event;
  }

  save() {
    if (this.validationStatusChanged) {
      this._contactRepository
        .update(this.editingContact, this.contact.id)
        .pipe(take(1))
        .subscribe(() => {
          this.openEdit = false;
          this.validationStatusChanged = false;
          this.existingContactUpdated = true;
          this.editingContact = null;
          this._contactRepository.getById(this.contact.id).subscribe(contact => {
            this.contact = contact;
          });
        });
    }
  }

  goBack() {
    const qp = this.route.snapshot.queryParamMap;
    const letter = qp.get('letter') || undefined;
    const anchorId = this.route.snapshot.paramMap.get('id') || qp.get('anchorId') || undefined;
    if (letter && anchorId) {
      this.router.navigate(['/kontakte'], { queryParams: { letter, anchorId } });
    } else if (window.history.length > 1) {
      this._location.back();
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
